import { getUrlFromS3XMLResponse } from "@hooks/useFileUpload";
import { useCallback } from "react";
import { $staffApi } from "../../../api";
import { fetchWithRetry } from "../../../utilities/api";

type UploadedMessageFile = { url: string; signature: string };

// Uploads in-memory reply attachments to S3 at submit time, returning the
// url/signature pairs the message endpoint expects.
export function useMessageFileUpload() {
  const { mutateAsync: getUploadPolicy } = $staffApi.useMutation("post", "/api/messages/upload/");

  const uploadToS3 = useCallback(
    async (file: File): Promise<UploadedMessageFile> => {
      const policyData = await getUploadPolicy({
        body: {
          name: file.name,
          type: file.type,
        },
      });

      const formData = new FormData();
      Object.entries(policyData).forEach(([key, value]) => {
        if (key !== "form_action" && key !== "signature") {
          formData.append(key, value as string);
        }
      });
      formData.append("file", file);

      const response = await fetchWithRetry(
        policyData.form_action,
        {
          method: "POST",
          body: formData,
        },
        { errorMessage: "File upload failed after retries" }
      );
      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const xmlText = await response.text();
      return { url: getUrlFromS3XMLResponse(xmlText), signature: policyData.signature };
    },
    [getUploadPolicy]
  );

  const getFileUploadData = useCallback(
    async (files: File[]): Promise<UploadedMessageFile[]> =>
      Promise.all(files.map((file) => uploadToS3(file))),
    [uploadToS3]
  );

  return { getFileUploadData };
}
